import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import ApiServices from '../Services/ApiServices';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlay,
  faEye,
  faBookOpen,
} from '@fortawesome/free-solid-svg-icons';
import { getStatusText, getStatusColor } from '../../Helpers/statusHelpers';
import { encryptId } from '../../utils/encryption';
import { storageHelper } from '../../Helpers/storageHelper';
import { useExamSecurity } from '../../Helpers/ioHelpers';
import Header from '../Shared/Header';
import SoftGrid from '../Shared/SoftGrid/SoftGrid';
import toastHelper from '../../Helpers/toastHelper';
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MyExam = () => {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    totalPages: 1,
    currentPage: 1,
    totalRecords: 0
  });
  const [searchWord, setSearchWord] = useState('');

  const fetchExams = async (word = '', page = 1) => {
    setLoading(true);
    try {
      const userId = storageHelper.getItem('user_id');
      if (!userId) throw new Error('User ID not found');
      const response = await ApiServices.get(
        `UserExam/getAll?PageSize=10&PageNumber=${page}&IsActive=true&Word=${encodeURIComponent(word)}`
      );
      if (response.check && response.data) {
        setExams(response.data.items);
        setPagination({
          totalPages: response.data.total_pages,
          currentPage: response.data.current_page,
          totalRecords: response.data.total_records
        });
      } else {
        setExams([]);
        setPagination({ totalPages: 1, currentPage: 1, totalRecords: 0 });
      }
    } catch (error) {

      setExams([]);
      setPagination({ totalPages: 1, currentPage: 1, totalRecords: 0 });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchExams(searchWord, pagination.currentPage);
  }, [searchWord, pagination.currentPage]);

  useExamSecurity(true);

  const handleStartExam = (examId) => {
    try {
      const encryptedId = encryptId(parseInt(examId, 10));
      if (!encryptedId) throw new Error('Failed to encrypt exam ID');
      navigate(`/exam/v1/${encryptedId}`);
    } catch (error) {
      toastHelper.error('حدث خطأ أثناء بدء الاختبار');
    }
  };

  const handleSearch = (word) => {
    setSearchWord(word);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const columns = [
    {
      key: 'title',
      label: 'عنوان الاختبار',
      sortable: true,
    },
    {
      key: 'subject',
      label: 'المادة',
      sortable: true,
      render: (value) => (
        <span className="mx-subject-tag">
          <FontAwesomeIcon icon={faBookOpen} />
          {value}
        </span>
      )
    },
    {
      key: 'teacher',
      label: 'المعلم',
      sortable: true,
    },
    {
      key: 'date',
      label: 'التاريخ',
      sortable: true,
      render: (value) => new Date(value).toLocaleDateString('ar-EG')
    },
    {
      key: 'duration',
      label: 'المدة',
      sortable: true,
      render: (value) => `${value} دقيقة`
    },
    {
      key: 'score',
      label: 'الدرجة',
      align: 'center',
      render: (value, row) => row.status !== 2 ? (
        <span className="mx-score-value">{value} <small>/ {row.total_questions}</small></span>
      ) : '-'
    },
    {
      key: 'exam_status',
      label: 'حالة الامتحان',
      align: 'center',
      render: (value, row) => (
        <span className="mx-status-indicator" style={{ background: getStatusColor(row.exam_status) }}>
          {getStatusText(row.exam_status)}
        </span>
      )
    },
    {
      key: 'status',
      label: 'حالة الطالب',
      align: 'center',
      render: (value, row) => (
        <span className="mx-status-indicator" style={{ background: getStatusColor(row.status) }}>
          {getStatusText(row.status)}
        </span>
      )
    }
  ];

  const actions = [
    {
      label: 'ابدأ الآن',
      icon: <FontAwesomeIcon icon={faPlay} />,
      className: 'mx-grid-action-btn start',
      hide: (row) => row.status !== 2 || row.exam_status === 3,
      onClick: (row) => handleStartExam(row.exam_id)
    },
    {
      label: 'النتيجة',
      icon: <FontAwesomeIcon icon={faEye} />,
      className: 'mx-grid-action-btn view',
      hide: (row) => row.exam_status !== 3,
      onClick: (row) => navigate(`/exam-result/${encryptId(row.exam_id)}`)
    }
  ];

  return (
    <>
      <Header />
      <br></br>
      <section className="mx-root" id="my-exams">
        <div className="mx-container">
          {/* ── header ── */}
          <motion.div
            className="mx-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <motion.div className="mx-eyebrow" variants={fadeUp} custom={0}>
              <span className="mx-eyebrow-dot" />
              اختباراتي التعليمية
            </motion.div>
            <motion.h2 className="mx-title" variants={fadeUp} custom={1}>
              لوحة <em>إنجازاتي واختباراتي</em>
            </motion.h2>
            <motion.p className="mx-subtitle" variants={fadeUp} custom={2}>
              تتبع مسيرتك التعليمية، راجع نتائجك، واستعد للتفوق في اختباراتك القادمة بكل ثقة
            </motion.p>
          </motion.div>

          {/* ── grid ── */}
          <div className="mx-grid-wrapper">
            <SoftGrid
              columns={columns}
              data={exams}
              isLoading={loading}
              onSearch={handleSearch}
              searchPlaceholder="ابحث بعنوان الاختبار أو المادة..."
              actions={actions}
              isPaginated={true}
              pageNumber={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalRecords={pagination.totalRecords}
              onPageChange={(page) => setPagination(prev => ({ ...prev, currentPage: page }))}
              pageSize={10}
              useExportButtons={false}
            />
          </div>
        </div>

        <style>{`
          .mx-root {
            font-family: "Zain", sans-serif;
            direction: rtl;
            position: relative;
            padding: 80px 0 100px;
            background: #fff;
            overflow: hidden;
            isolation: isolate;
          }

          .mx-container {
            position: relative; z-index: 2;
            max-width: 1260px; margin: 0 auto; padding: 0 40px;
          }

          /* Header */
          .mx-header { text-align: center; margin-bottom: 40px; }
          .mx-eyebrow {
            display: inline-flex; align-items: center; gap: 10px;
            color: #6366f1; font-weight: 800; font-size: 0.82rem;
            letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 22px;
          }
          .mx-eyebrow-dot {
            width: 8px; height: 8px; border-radius: 50%; background: #6366f1;
            animation: mx-pulse 2.2s ease-in-out infinite;
          }
          @keyframes mx-pulse {
            0%,100%{ box-shadow: 0 0 0 0 rgba(232,146,10,0.45); }
            50%{ box-shadow: 0 0 0 9px rgba(232,146,10,0); }
          }
          .mx-title {
            font-size: clamp(2.2rem, 4vw, 3.6rem);
            font-weight: 900; color: #0f172a;
            margin: 0 0 18px; line-height: 1.2;
          }
          .mx-title em {
            font-style: normal;
            background: linear-gradient(90deg, #6366f1 0%, #6366f1 55%, #6366f1 100%);
            -webkit-background-clip: text; -webkit-text-fill-color: transparent;
            background-clip: text;
          }
          .mx-subtitle {
            font-size: 1.08rem; color: #64748b; line-height: 1.85; margin: 0;
          }

          .mx-subject-tag {
            display: inline-flex; align-items: center; gap: 6px;
            background: #f1f5f9;
            color: #475569;
            padding: 4px 12px;
            border-radius: 8px;
            font-size: 0.85rem;
            font-weight: 700;
          }

          .mx-status-indicator {
            padding: 4px 12px;
            border-radius: 8px;
            font-size: 0.8rem;
            font-weight: 800;
            color: #fff;
            white-space: nowrap;
          }

          .mx-score-value { font-size: 1.1rem; font-weight: 900; color: #0f172a; }
          .mx-score-value small { color: #94a3b8; font-size: 0.8rem; }

          .mx-grid-action-btn {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 6px 14px;
            border-radius: 10px;
            font-family: inherit;
            font-weight: 800;
            font-size: 0.85rem;
            cursor: pointer;
            border: none;
            transition: all 0.2s;
            color: #fff;
          }
          .mx-grid-action-btn.start { background: #6366f1; }
          .mx-grid-action-btn.view { background: #6366f1; }
          .mx-grid-action-btn:hover { opacity: 0.9; transform: scale(1.02); }

          @media(max-width:768px){
            .mx-root { padding: 60px 0 80px; }
            .mx-container { padding: 0 20px; }
            .mx-header { margin-bottom: 30px; }
          }
        `}</style>
      </section>
    </>
  );
};

export default MyExam;